import { createFileRoute, Link } from "@tanstack/react-router";
import { breadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";
import { serviceJsonLd } from "@/lib/service-jsonld";
import { faqPageJsonLd, type FaqItem } from "@/lib/faq-jsonld";
import { FaqAccordion } from "@/components/site/FaqAccordion";

const elektroFaqs: FaqItem[] = [
  {
    q: "Zijn jullie een erkend elektrotechnisch installateur?",
    a: "Ja. Onze monteurs werken volgens NEN 1010 en zijn erkend voor het plaatsen en vervangen van groepenkasten, laadpalen en overige vaste elektra-installaties in woningen en bedrijfspanden.",
  },
  {
    q: "Kunnen jullie mijn groepenkast op dezelfde dag vervangen?",
    a: "In de meeste gevallen wel. Een standaard vervanging duurt 3 tot 5 uur. We nemen vooraf foto’s door en stemmen af of er extra groepen, aardlekautomaten of een verzwaring nodig zijn.",
  },
  {
    q: "Kan ik van 1-fase naar 3-fase overstappen via jullie?",
    a: "Ja. Wij regelen de aanvraag bij de netbeheerder, bouwen de meterkast om naar 3-fase en verdelen de groepen opnieuw zodat laadpaal, inductie of warmtepomp probleemloos werken.",
  },
  {
    q: "Werken jullie ook in mijn regio?",
    a: "We werken landelijk vanuit vaste servicepunten in Amsterdam, Rotterdam, Den Haag, Utrecht, Eindhoven, Tilburg, Almere, Nijmegen en Arnhem. Zit u er niet bij? Vraag gerust een offerte aan.",
  },
  {
    q: "Krijg ik garantie op de installatie?",
    a: "Ja, u ontvangt fabrieksgarantie op de materialen en installatiegarantie op ons werk. Bij oplevering krijgt u een installatierapport en foto’s van de kast voor uw administratie.",
  },
];
import {
  Camera,
  Zap,
  Plug,
  BatteryCharging,
  Trees,
  Store,
  ShieldCheck,
  Wrench,
  PhoneCall,
  CheckCircle2,
  ArrowRight,
  ClipboardList,
  CalendarCheck,
  HardHat,
} from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ContactCta } from "@/components/site/ContactCta";
import installatieCameraImg from "@/assets/installatie-camera.png.asset.json";



export const Route = createFileRoute("/elektrotechniek")({
  head: () => ({
    meta: [
      { title: "Elektrotechniek — Elvion Group Nederland helpt u graag verder" },
      {
        name: "description",
        content:
          "Erkende installateurs voor camerasystemen, groepenkasten, laadpalen, 1-fase naar 3-fase en tuinverlichting. NEN 1010, landelijk actief in heel Nederland.",
      },
      { property: "og:title", content: "Elektrotechniek — Elvion Group Nederland helpt u graag verder" },
      {
        property: "og:description",
        content:
          "Van groepenkast tot complete winkelrenovatie. Elvion Group Nederland helpt u graag verder — netjes, veilig en NEN 1010 conform.",
      },
      { property: "og:url", content: "https://elvion-group.nl/elektrotechniek" },
    ],
    links: [{ rel: "canonical", href: "https://elvion-group.nl/elektrotechniek" }],
    scripts: [
      breadcrumbJsonLd([{ name: "Elektrotechniek", path: "/elektrotechniek" }]),
      serviceJsonLd({
        path: "/elektrotechniek",
        name: "Elektrotechniek door Elvion Group Nederland",
        description:
          "Erkende installateurs voor camerasystemen, groepenkasten, 1-fase naar 3-fase, laadpalen, tuinverlichting en winkelrenovaties. Altijd NEN 1010 conform.",
        serviceType: "Elektrotechniek",
        offerCatalog: [
          "Camerasystemen",
          "Groepenkasten",
          "Uitbreiding groepenkast",
          "1-fase naar 3-fase",
          "Elektra in de tuin",
          "Winkelrenovaties",
        ],
      }),
      faqPageJsonLd(elektroFaqs, "https://elvion-group.nl/elektrotechniek#faq"),
    ],
  }),
  component: InstallatiePage,
});

const services = [
  {
    icon: Camera,
    title: "Camerasystemen",
    body: "Complete IP-camerasystemen van Hikvision en Dahua. Wij verzorgen bekabeling (UTP/PoE), montage, NVR-configuratie, app-koppeling en uitleg op locatie.",
  },
  {
    icon: Zap,
    title: "Groepenkasten",
    body: "Plaatsing en vervanging van voorgemonteerde groepenkasten met aardlekautomaten (ALS). Altijd NEN 1010 conform, afgestemd op woning of bedrijfspand.",
  },
  {
    icon: Plug,
    title: "Uitbreiding groepenkast",
    body: "Extra groep nodig voor een laadpaal, inductie- of elektrisch koken, airco of warmtepomp? Wij breiden je bestaande kast uit en leveren de juiste beveiliging.",
  },
  {
    icon: BatteryCharging,
    title: "1-fase naar 3-fase",
    body: "Verzwaring van je aansluiting in overleg met de netbeheerder, inclusief het ombouwen van de meterkast en groepenverdeling naar 3-fase (krachtstroom).",
  },
  {
    icon: Trees,
    title: "Elektra in de tuin",
    body: "Aanleg van buitenstopcontacten, tuinverlichting, vijverpompen en laadpunten. Waterdicht, vorstvrij en netjes weggewerkt in de grond.",
  },
  {
    icon: Store,
    title: "Winkelrenovaties",
    body: "Complete renovaties op het gebied van elektrotechniek, beveiliging en automatisering — van verlichting en kassagroepen tot toegang en camera's.",
  },
];

const process = [
  {
    icon: PhoneCall,
    title: "1. Vrijblijvend contact",
    body: "Bel of stuur een aanvraag. We bespreken kort wat je nodig hebt en plannen indien gewenst een schouw op locatie.",
  },
  {
    icon: ClipboardList,
    title: "2. Offerte op maat",
    body: "Je ontvangt een transparante offerte met materiaal, arbeid en planning. Geen verrassingen achteraf.",
  },
  {
    icon: CalendarCheck,
    title: "3. Inplannen",
    body: "Akkoord? Dan plannen we de werkzaamheden op een moment dat jou uitkomt — ook in de avond of het weekend.",
  },
  {
    icon: HardHat,
    title: "4. Vakkundige uitvoering",
    body: "Onze gecertificeerde monteurs voeren het werk netjes uit en leveren een installatie op die voldoet aan NEN 1010.",
  },
];

const certifications = [
  "NEN 1010 conform werken",
  "VCA-gecertificeerde monteurs",
  "Erkend installatiebedrijf",
  "Garantie op werk en materiaal",
  "Eigen voorraad A-merken",
  "Landelijk werkgebied",
];

function InstallatiePage() {
  return (
    <>
      <Header variant="shop" />
      <main>

        <ContactCta
          imageUrl={installatieCameraImg.url}
          imageAlt="Elvion Group Nederland monteurs installeren een beveiligingscamera in een winkel"
          title="Installatie inplannen door onze monteurs?"
          description="Onze erkende installateurs verzorgen de complete installatie op locatie: van groepenkasten en camerasystemen tot laadpalen en netwerkapparatuur. Vakkundig, veilig en zonder gedoe."
        />

        {/* Hero */}

        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-orange/30 bg-orange/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-orange">
                <Wrench className="h-3.5 w-3.5" />
                Erkende installateurs
              </span>
              <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
                Installatietechniek voor elektra, beveiliging & automatisering
              </h1>
              <p className="mt-4 max-w-xl text-base text-text-secondary sm:text-lg">
                Van een extra groep in de meterkast tot een complete winkelrenovatie:
                Elvion Group Nederland installeert het vakkundig, veilig en NEN 1010 conform.
                Onze monteurs werken landelijk en leveren altijd een nette,
                opgeruimde installatie op.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/offerte-aanvragen"
                  className="inline-flex items-center gap-2 rounded-md bg-orange px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange/90"
                >
                  Vraag een offerte aan
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/klantenservice"
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:border-orange hover:text-orange"
                >
                  Plan een schouw
                </Link>
              </div>
              <ul className="mt-6 grid gap-2 text-sm text-text-secondary sm:grid-cols-2">
                {certifications.slice(0, 4).map((c) => (
                  <li key={c} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-orange" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-orange/20 via-orange/5 to-transparent blur-2xl" />
              <div className="relative rounded-2xl border border-border bg-card p-8 shadow-lg">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: Zap, label: "Groepenkasten" },
                    { icon: Camera, label: "Camera's" },
                    { icon: BatteryCharging, label: "Laadpalen" },
                    { icon: Store, label: "Winkels" },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex flex-col items-center gap-3 rounded-xl border border-border bg-background p-5 text-center"
                    >
                      <div className="rounded-full bg-orange/10 p-3 text-orange">
                        <item.icon className="h-6 w-6" />
                      </div>
                      <span className="text-sm font-semibold">{item.label}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 rounded-xl bg-background p-5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-orange">
                    Direct schakelen?
                  </p>
                  <p className="mt-1 text-sm text-text-secondary">
                    Bel ons voor een vrijblijvend gesprek of een snelle prijsindicatie.
                  </p>
                  <a
                    href="tel:+31000000000"
                    className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-foreground hover:text-orange"
                  >
                    <PhoneCall className="h-4 w-4" />
                    Neem contact op
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services grid */}
        <section className="border-t border-border bg-card/30">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                Wat installeren wij?
              </h2>
              <p className="mt-3 text-base text-text-secondary">
                Onze monteurs zijn breed inzetbaar — van kleine uitbreidingen tot
                grote projecten op het gebied van elektrotechniek, beveiliging en
                automatisering.
              </p>
            </div>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {services.map((s) => (
                <div
                  key={s.title}
                  className="group rounded-xl border border-border bg-background p-6 transition-all hover:border-orange hover:shadow-md"
                >
                  <div className="inline-flex rounded-lg bg-orange/10 p-3 text-orange transition-colors group-hover:bg-orange group-hover:text-white">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 text-lg font-bold">{s.title}</h3>
                  <p className="mt-2 text-sm text-text-secondary">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Zo werkt onze installatietechniek
            </h2>
            <p className="mt-3 text-base text-text-secondary">
              Een transparant traject in vier stappen — van eerste contact tot oplevering.
            </p>
          </div>

          <ol className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {process.map((step) => (
              <li
                key={step.title}
                className="relative rounded-xl border border-border bg-card p-6"
              >
                <div className="inline-flex rounded-lg bg-orange/10 p-3 text-orange">
                  <step.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-base font-bold">{step.title}</h3>
                <p className="mt-2 text-sm text-text-secondary">{step.body}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Detailed sections */}
        <section className="border-t border-border bg-card/30">
          <div className="mx-auto max-w-7xl space-y-12 px-4 py-14 sm:px-6 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-2">
              <div>
                <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                  Camerasystemen die écht werken
                </h2>
                <p className="mt-3 text-text-secondary">
                  Wij ontwerpen en installeren camerasystemen op maat. Of het nu
                  gaat om een woning, kantoor, magazijn of winkel — we kijken
                  samen naar dekking, opnameduur en risicogebieden. Vervolgens
                  verzorgen wij de bekabeling, montage, configuratie van de NVR
                  en de koppeling met de Hik-Connect of DMSS app.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-text-secondary">
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Hikvision & Dahua A-merken</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> 4 MP tot 8 MP IP-camera's met AcuSense / WizSense</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Inclusief AVG-bordjes en privacy-instellingen</li>
                </ul>
              </div>
              <div>
                <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                  Groepenkast plaatsen of uitbreiden
                </h2>
                <p className="mt-3 text-text-secondary">
                  Een veilige meterkast is het hart van iedere installatie. Wij
                  plaatsen voorgemonteerde groepenkasten van AEG,
                  uitgevoerd met aardlekautomaten conform de nieuwste NEN 1010.
                  Heb je extra groepen nodig voor een laadpaal, inductiekookplaat,
                  airco of warmtepomp? Dan breiden we je bestaande kast netjes uit.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-text-secondary">
                <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Inspectie en risico-analyse op locatie</li>
                <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Volledig NEN 1010 conform opgeleverd</li>
                <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Inclusief opleverrapport en stickers</li>
              </ul>
              <p className="mt-4 text-sm text-text-secondary">
                Werkzaam in onder andere{" "}
                <Link to="/groepenkast-vervangen/$stad" params={{ stad: "rotterdam" }} className="font-medium text-foreground hover:text-orange hover:underline">Rotterdam</Link>,{" "}
                <Link to="/groepenkast-vervangen/$stad" params={{ stad: "utrecht" }} className="font-medium text-foreground hover:text-orange hover:underline">Utrecht</Link>,{" "}
                <Link to="/groepenkast-vervangen/$stad" params={{ stad: "tilburg" }} className="font-medium text-foreground hover:text-orange hover:underline">Tilburg</Link>{" "}
                en{" "}
                <Link to="/groepenkast-vervangen/$stad" params={{ stad: "nijmegen" }} className="font-medium text-foreground hover:text-orange hover:underline">Nijmegen</Link>.
              </p>
            </div>
              <div>
                <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                  Van 1-fase naar 3-fase (krachtstroom)
                </h2>
                <p className="mt-3 text-text-secondary">
                  Heb je meer vermogen nodig voor een laadpaal, warmtepomp,
                  industriële apparatuur of een werkplaats? Wij verzorgen de
                  complete overstap naar een 3-fase aansluiting: we vragen de
                  verzwaring aan bij de netbeheerder, bouwen de meterkast om
                  en herverdelen de groepen over de drie fasen voor een
                  optimale belasting.
                </p>
              </div>
              <div>
                <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                  Elektra aanleggen in de tuin
                </h2>
                <p className="mt-3 text-text-secondary">
                  Buitenstopcontacten, sfeerverlichting, een vijverpomp of een
                  laadpunt op de oprit? Wij leggen waterdicht en vorstvrij
                  elektra aan in de tuin, met grondkabel (YMvKas) op de juiste
                  diepte en aparte groep in de meterkast. Alles netjes
                  weggewerkt en aangesloten.
                </p>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-background p-8 lg:p-10">
              <div className="flex items-start gap-4">
                <div className="hidden rounded-lg bg-orange/10 p-3 text-orange sm:inline-flex">
                  <Store className="h-7 w-7" />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                    Complete winkelrenovaties
                  </h2>
                  <p className="mt-3 text-text-secondary">
                    Een nieuwe winkel openen of uw bestaande zaak verbouwen?
                    Elvion Group Nederland helpt u graag verder en neemt het
                    complete elektrotechnische en beveiligingstraject uit
                    handen. We werken samen met aannemers, interieurbouwers
                    en winkelketens door heel Nederland — vaak buiten
                    openingstijden, zodat uw bedrijf gewoon door kan draaien.
                  </p>
                  <div className="mt-6 grid gap-4 sm:grid-cols-3">
                    <div className="rounded-lg border border-border p-4">
                      <ShieldCheck className="h-5 w-5 text-orange" />
                      <p className="mt-2 text-sm font-semibold">Beveiliging</p>
                      <p className="mt-1 text-xs text-text-secondary">
                        Camera's, alarminstallatie en toegangscontrole.
                      </p>
                    </div>
                    <div className="rounded-lg border border-border p-4">
                      <Zap className="h-5 w-5 text-orange" />
                      <p className="mt-2 text-sm font-semibold">Elektrotechniek</p>
                      <p className="mt-1 text-xs text-text-secondary">
                        Verdeelinrichting, krachtstroom, kassagroepen en verlichting.
                      </p>
                    </div>
                    <div className="rounded-lg border border-border p-4">
                      <Wrench className="h-5 w-5 text-orange" />
                      <p className="mt-2 text-sm font-semibold">Automatisering</p>
                      <p className="mt-1 text-xs text-text-secondary">
                        Schakelklokken, sensoren, KNX en domotica oplossingen.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why us */}
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Waarom Elvion Group Nederland u helpt
          </h2>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {certifications.map((c) => (
              <div
                key={c}
                className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3"
              >
                <CheckCircle2 className="h-5 w-5 shrink-0 text-orange" />
                <span className="text-sm font-medium">{c}</span>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="border-t border-border">
          <div className="mx-auto max-w-7xl px-4 py-14 text-center sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Klaar om jouw project te bespreken?
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-base text-text-secondary">
              Vraag vrijblijvend een offerte aan of plan een schouw op locatie.
              We denken mee over de slimste en veiligste oplossing voor jouw
              situatie.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/offerte-aanvragen"
                className="inline-flex items-center gap-2 rounded-md bg-orange px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange/90"
              >
                Offerte opvragen
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/klantenservice"
                className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:border-orange hover:text-orange"
              >
                Contact opnemen
              </Link>
            </div>
          </div>
        </section>
        <section className="border-t border-border bg-background py-16">
          <div className="container mx-auto px-4">
            <FaqAccordion
              items={elektroFaqs}
              title="Veelgestelde vragen — elektrotechniek"
              intro="Antwoorden op vragen die we vaak krijgen over groepenkasten, laadpalen en elektrawerk."
              id="faq-elektro"
            />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
